import { format } from "date-fns";
import useTranslation from "../../hooks/useTranslation";
import SurveyDispatch from "../../models/surveyDispatch";
import {
  GetDemographicGroupsResponse,
  GetQuestionBreakdownResponse,
  GetSurveyChapterScoringResponse,
} from "../../types/workplaceCulture";
import { ReportChapterScores } from "../ReportChapterScores/ReportChapterScores";
import { ReportCustomQuestion } from "../ReportCustomQuestion/ReportCustomQuestion";
import { ReportDemographicComposition } from "../ReportDemographicComposition/ReportDemographicComposition";
import { ReportInclusionExplanation } from "../ReportInclusionExplanation/ReportInclusionExplanation";
import { ReportIntro } from "../ReportIntro/ReportIntro";
import { ReportMajorityMinority } from "../ReportMajorityMinority/ReportMajorityMinority";
import { ReportMethodology } from "../ReportMethodology/ReportMethodology";
import { ReportQuestionBreakdown } from "../ReportQuestionBreakdown/ReportQuestionBreakdown";
import { ReportSpecificAttention } from "../ReportSpecificAttention/ReportSpecificAttention";
import { Button } from "../ui/Button/Button";
import { Typography } from "../ui/Typography/Typography";
import "./ReportContainer.scss";

interface ReportContainerProps {
  surveyDispatch: SurveyDispatch;
  chapterScoringData?: GetSurveyChapterScoringResponse;
  questionsData?: GetQuestionBreakdownResponse;
  demographicsData?: GetDemographicGroupsResponse;
  companyName: string;
}

export const ReportContainer = ({
  surveyDispatch,
  chapterScoringData,
  questionsData,
  demographicsData,
  companyName,
}: ReportContainerProps) => {
  const { t } = useTranslation("reportPage");
  const surveyPeriod = surveyDispatch.launchDate
    ? `${format(surveyDispatch.launchDate, "MMMM dd")} - ${format(
        surveyDispatch.dueDate || new Date(),
        "MMMM dd, yyyy"
      )}`
    : "Survey Period";

  // Filter custom demographic groups (CDEG) once
  const customDemographicGroups =
    demographicsData?.demographic_groups.filter((group) =>
      group.id.startsWith("CDEG")
    ) || [];

  return (
    <div className="ReportContainer">
      <div className="ReportContainer__header">
        <Typography
          tagVariant="h1"
          desktop="h1"
          className="ReportContainer__header__title"
          color="darkerStoneBlue"
        >
          {t("title", { companyName })}
        </Typography>
        <Typography
          desktop="body1"
          className="ReportContainer__period"
          color="secondary"
        >
          {t("surveyPeriod", { period: surveyPeriod })}
        </Typography>
      </div>

      <section className="ReportContainer__section">
        <ReportIntro
          companyName={companyName}
          demographicsData={demographicsData}
          chapterScoringData={chapterScoringData}
        />
      </section>

      <section className="ReportContainer__section">
        <ReportChapterScores chapterScoringData={chapterScoringData} />
      </section>

      {/* Print-only version of ReportInclusionExplanation */}
      <section className="ReportContainer__section ReportContainer__section--printOnly">
        <ReportInclusionExplanation />
      </section>

      <section className="ReportContainer__section ReportContainer__section--pageBreak">
        <div className="ReportContainer__methodologyGrid">
          <div className="ReportContainer__methodologyGrid__sidebar">
            <ReportMethodology
              surveyDispatch={surveyDispatch}
              demographicsData={demographicsData}
            />
            <div className="ReportContainer__section--screenOnly">
              <ReportInclusionExplanation />
            </div>
          </div>
          <div className="ReportContainer__methodologyGrid__main">
            <ReportDemographicComposition
              demographicsData={demographicsData}
              dispatchId={surveyDispatch.id}
            />
          </div>
        </div>
      </section>

      <section className="ReportContainer__section">
        <ReportMajorityMinority demographicsData={demographicsData} />
      </section>

      <section className="ReportContainer__section">
        <ReportSpecificAttention demographicsData={demographicsData} />
      </section>

      <section className="ReportContainer__section ReportContainer__section--pageBreak">
        <ReportQuestionBreakdown
          questionsData={questionsData}
          chapterScoringData={chapterScoringData}
        />
      </section>
      {customDemographicGroups.length > 0 && (
        <section className="ReportContainer__section">
          <ReportCustomQuestion
            customDemographicGroups={customDemographicGroups}
            dispatchToken={surveyDispatch.id}
          />
        </section>
      )}

      {/* Print/Export Controls */}
      <div className="ReportContainer__controls">
        <Button
          onClick={() => window.print()}
          className="ReportContainer__printButton"
          variant="contained"
        >
          {t("controls.printReport")}
        </Button>
      </div>
    </div>
  );
};
